import React from 'react';
import { Home, Play, Film, Heart, Settings, Tv, X, List, Star, User, ChevronDown, ChevronRight } from 'lucide-react';

const Sidebar = ({ groups, selectedGroup, onSelectGroup, isOpen, onClose, contentType, onContentTypeChange, user }) => {
  const [isCategoriesCollapsed, setIsCategoriesCollapsed] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');
  // Sezioni fisse per la sidebar con funzionalità TV LIVE e FILM
  const sections = [
    { 
      id: 'live', 
      name: 'TV LIVE', 
      icon: Tv,
      onClick: () => onContentTypeChange && onContentTypeChange('LIVE')
    },
    { 
      id: 'movies', 
      name: 'FILM', 
      icon: Film,
      onClick: () => onContentTypeChange && onContentTypeChange('FILM')
    },
    { id: 'favorites', name: 'Preferiti', icon: Heart },
    {
      id: 'playlists',
      name: 'Gestisci playlist',
      icon: Settings,
      onClick: () => {
        // Usa il metodo globale per aprire il PlaylistManager
        if (window.setShowPlaylistManager) {
          window.setShowPlaylistManager(true);
        } else {
          // Fallback: emette un evento
          window.dispatchEvent(new Event('openPlaylistManager'));
        }
      }
    },
    ...(user?.role === 'admin'
      ? [{
          id: 'users',
          name: 'Gestione Utenze',
          icon: User,
          onClick: () => {
            if (window.setShowUserManagement) {
              window.setShowUserManagement(true);
            } else {
              window.dispatchEvent(new Event('openUserManagement'));
            }
          }
        }]
      : [])
  ];

  const categoryIcons = {
    'All': Home,
    'Sports': Play,
    'Movies': Film,
    'News': Play,
    'Kids': Star,
    'Documentary': Play,
    'Entertainment': Play,
    'Music': Play,
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:sticky top-0 left-0 h-screen w-64 bg-gray-950/90 backdrop-blur-sm border-r border-gray-800/50 z-50 transition-transform duration-300 lg:translate-x-0 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="p-4 border-b border-gray-800/50 lg:hidden">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold text-white">Menu</h2>
              <button
                onClick={onClose}
                className="text-white hover:bg-white/10 rounded-lg p-1 transition-colors duration-200"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Main Sections */}
          <div className="flex-1 overflow-y-auto p-4">
            <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-4 hidden lg:block">
              Sezioni
            </h3>
            
            {/* Tabs for TV LIVE and FILM */}
            <div className="flex mb-6 border-b border-gray-700">
              {sections.filter(s => ['live', 'movies'].includes(s.id)).map((section) => {
                const Icon = section.icon;
                const isSelected = (section.id === 'live' && contentType === 'LIVE') || 
                                 (section.id === 'movies' && contentType === 'FILM');
                
                return (
                  <button
                    key={section.id}
                    onClick={() => {
                      section.onClick();
                      onClose();
                    }}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-all duration-200 relative ${
                      isSelected
                        ? 'text-blue-400'
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    <Icon size={18} />
                    <span>{section.name}</span>
                    {isSelected && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500 rounded-full" />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Other sections */}
            <nav className="space-y-1 mb-6">
              {sections.filter(s => !['live', 'movies'].includes(s.id)).map((section) => {
                const Icon = section.icon;
                const isSelected = selectedGroup === section.id;
                
                return (
                  <button
                    key={section.id}
                    onClick={() => {
                      if (section.onClick) {
                        section.onClick();
                      } else {
                        onSelectGroup(section.id);
                      }
                      onClose();
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all duration-200 ${
                      isSelected
                        ? 'bg-gradient-to-r from-blue-600/20 to-purple-600/20 border border-blue-500/30 text-blue-400'
                        : 'text-gray-300 hover:text-white hover:bg-gray-800/50'
                    }`}
                  >
                    <Icon size={18} />
                    <span className="text-sm font-medium">{section.name}</span>
                    {isSelected && (
                      <div className="ml-auto w-2 h-2 bg-blue-500 rounded-full" />
                    )}
                  </button>
                );
              })}
            </nav>

            {/* Categories */}
            {groups && groups.length > 0 && (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Categorie
                  </h3>
                  <button
                    onClick={() => setIsCategoriesCollapsed(!isCategoriesCollapsed)}
                    className="text-gray-400 hover:text-white transition-colors duration-200"
                  >
                    {isCategoriesCollapsed ? (
                      <ChevronRight size={16} />
                    ) : (
                      <ChevronDown size={16} />
                    )}
                  </button>
                </div>
                {!isCategoriesCollapsed && (
                  <div className="relative mb-3">
                    <input
                      type="text"
                      placeholder="Cerca categoria..."
                      className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                      <button
                        onClick={() => setSearchTerm('')}
                        className="absolute right-2 top-2 text-gray-400 hover:text-white"
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                )}
                <nav className={`space-y-1 pb-4 ${isCategoriesCollapsed ? 'hidden' : ''}`}>
                  {groups
                    .filter(g => 
                      searchTerm === '' || 
                      g.name.toLowerCase().includes(searchTerm.toLowerCase())
                    )
                    .map((g) => {
                    const Icon = categoryIcons[g.name] || Home;
                    const isSelected = selectedGroup === g.name;
                    
                    return (
                      <button
                        key={g.name}
                        onClick={() => {
                          onSelectGroup(g.name);
                          onClose();
                        }}
                        className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-left transition-all duration-200 ${
                          isSelected
                            ? 'bg-gradient-to-r from-blue-600/20 to-purple-600/20 border border-blue-500/30 text-blue-400'
                            : 'text-gray-300 hover:text-white hover:bg-gray-800/50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Icon size={18} />
                          <span className="text-sm font-medium">{g.name}</span>
                        </div>
                        <span className="text-xs opacity-70">{g.count}</span>
                        {isSelected && (
                          <div className="ml-2 w-2 h-2 bg-blue-500 rounded-full" />
                        )}
                      </button>
                    );
                  })}
                </nav>
              </>
            )}
          </div>

          {/* Footer spacer per riempire lo spazio rimanente */}
          <div className="p-4 border-t border-gray-800/50 mt-auto">
            <div className="text-xs text-gray-500 text-center">
              KainoTV
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
